import { useState } from "react";
import { LinkIcon } from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import { ICustomDomainLinkableResponse } from "shared";

import { Button } from "../../../../../common/Atoms/Button";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Select } from "../../../../../common/Atoms/Select";
import { useLinkCustomDomainMutation } from "../../../../services/api/customDomainApi/customDomain";

interface LinkCustomDomainProps {
  organisationId: string;
  /** Verified domains in other organisations the user administers */
  linkable: ICustomDomainLinkableResponse[`linkable`];
  /** The domain being typed, when it is already verified by an org the user cannot link from */
  blockedDomain?: string;
}

/**
 * Lets the org reuse a domain already verified in another organisation the
 * user administers, with no new DNS records. Shown as soon as there is
 * something to pick from. When the typed domain is verified by an org the user
 * has no access to, explains who can grant it.
 */
export function LinkCustomDomain({
  organisationId,
  linkable,
  blockedDomain,
}: LinkCustomDomainProps) {
  const [link, { isLoading }] = useLinkCustomDomainMutation();
  const [sourceOrganisationId, setSourceOrganisationId] = useState<string>(linkable[0]?.organisationId || ``);

  const selected = linkable.find(l => l.organisationId === sourceOrganisationId) || linkable[0];

  async function handleLink() {
    if (!selected) {
      return;
    }

    try {
      await link({
        organisationId,
        sourceOrganisationId: selected.organisationId,
      }).unwrap();

      toast.success(`Domain linked. Emails will send from ${selected.domain}.`);
    }
    catch (err) {
      toast.error(err?.data?.message || `Could not link the domain. Please try again.`);
    }
  }

  return (
    <>
      { linkable.length > 0 && (
        <Notification
          type={ `info` }
          className={ `mt-4` }
          title={ `Use a domain you've already verified` }
        >
          <div className={ `space-y-3` }>
            <p>
              { `Link a domain that is verified in another organisation you administer. No DNS changes are needed.` }
            </p>
            <Select
              label={ `Verified domain` }
              options={ linkable.map(l => ({ label: `${l.domain} (${l.name})`, value: l.organisationId })) }
              selected={ selected?.organisationId }
              onChange={ e => setSourceOrganisationId(String(e.value)) }
            />
            <Button
              onClick={ handleLink }
              loading={ isLoading }
              disabled={ isLoading || !selected }
              icon={ <LinkIcon className={ `h-4 w-4 ml-2` } /> }
            >
              { `Link domain` }
            </Button>
          </div>
        </Notification>
      ) }
      { blockedDomain && (
        <Notification
          type={ `warning` }
          className={ `mt-4` }
          title={ `${blockedDomain} is already verified by another organisation` }
        >
          { `Ask an admin of that organisation to give you settings access, then you can link its verified domain here without changing DNS.` }
        </Notification>
      ) }
    </>
  );
}
